import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errorHandling';
import {
  SCHOOL_CODE_METADATA_KEY,
  clearPendingSchoolCode,
  normaliseSchoolCode,
  readPendingSchoolCode,
} from '../lib/pendingSchoolCode';

/**
 * School passes (20260901000004): a school buys all-access with one redeem
 * code; access is a READ-TIME entitlement, never materialised as enrollments
 * (enrollment rows are created lazily on first course-open by the
 * enroll_in_course RPC, which checks pass coverage server-side).
 *
 * Coverage semantics: `courseIds === null` means the whole published
 * catalogue; otherwise the pass covers exactly those course ids (its bundle's
 * membership, resolved server-side). `passCoversCourse` below is the one
 * client-side implementation of the coverage question — never re-derive it
 * inline, the server's user_pass_covers_course stays the authority.
 */

export interface SchoolPassScope {
  passId: string;
  schoolName: string;
  bundleId: string | null;
  expiresAt: Date;
  /** null = the whole published catalogue */
  courseIds: string[] | null;
}

export interface RedeemResult {
  passId: string;
  schoolName: string;
  bundleId: string | null;
  expiresAt: Date;
  alreadyMember: boolean;
}

export interface SchoolPassOverviewRow {
  schoolId: string;
  schoolName: string;
  contactName: string | null;
  contactEmail: string | null;
  /** null when the school has no passes yet (LEFT JOIN row) */
  passId: string | null;
  code: string | null;
  bundleId: string | null;
  bundleTitle: string | null;
  seatCap: number | null;
  seatsUsed: number;
  startsAt: Date | null;
  expiresAt: Date | null;
  status: 'active' | 'revoked' | null;
}

/** The active pass covering this course, if any (drives "Covered by <school>" UI). */
export function coveringPass(
  passes: SchoolPassScope[],
  courseId: string,
): SchoolPassScope | undefined {
  return passes.find((p) => p.courseIds === null || p.courseIds.includes(courseId));
}

export function passCoversCourse(passes: SchoolPassScope[], courseId: string): boolean {
  return !!coveringPass(passes, courseId);
}

/** The active pass covering this bundle, if any — an all-access pass or one issued for it. */
export function bundleCoveringPass(
  passes: SchoolPassScope[],
  bundleId: string,
): SchoolPassScope | undefined {
  return passes.find((p) => p.bundleId === null || p.bundleId === bundleId);
}

/** Does any active pass cover the whole catalogue? Then no pack is worth offering. */
export function hasAllAccessPass(passes: SchoolPassScope[]): boolean {
  return passes.some((p) => p.courseIds === null);
}

/**
 * The bundle a pass-holder should land on after signing in: the pack their
 * school issued the pass for (Exam Ready 2026 → its lessons bundle). When
 * several passes name a bundle, the longest-running one wins. Null for an
 * all-access pass — there is no single pack to start on, so /browse it is.
 */
export function passLandingBundleId(passes: SchoolPassScope[]): string | null {
  const scoped = passes.filter((p) => p.bundleId);
  if (scoped.length === 0) return null;
  scoped.sort((a, b) => b.expiresAt.getTime() - a.expiresAt.getTime());
  return scoped[0].bundleId;
}

/**
 * Pass-holders see no prices anywhere in the student app: their school has
 * paid, and a shelf of rand amounts reads as "you still have to buy this".
 * True while the pass read is in flight too — a pass-holder must never see a
 * price flash before the pass lands; an ordinary student just sees prices a
 * beat later. Staff always see prices.
 */
export function shouldHidePrices(
  isStudent: boolean,
  passes: SchoolPassScope[],
  passesLoading: boolean,
): boolean {
  return isStudent && (passesLoading || passes.length > 0);
}

export function passCoversBundle(passes: SchoolPassScope[], bundleId: string): boolean {
  return !!bundleCoveringPass(passes, bundleId);
}

// The redeem RPC raises these tokens; everything else surfaces as-is.
const REDEEM_ERRORS: Record<string, string> = {
  invalid_code: "That code doesn't match a school pass on this site. Check it and try again.",
  pass_revoked: 'This school code has been deactivated. Ask your school for the current code.',
  pass_expired: 'This school pass has ended. Ask your school about renewing it.',
  pass_not_started: "This school pass isn't active yet.",
  pass_full: 'This school pass has reached its seat limit. Ask your school to raise it.',
};

function mapScope(row: any): SchoolPassScope {
  return {
    passId: row.pass_id,
    schoolName: row.school_name,
    bundleId: row.bundle_id || null,
    expiresAt: new Date(row.expires_at),
    courseIds: row.course_ids ?? null,
  };
}

export const schoolPassService = {
  /** The caller's active passes and what each covers. */
  async getMyActivePasses(): Promise<SchoolPassScope[]> {
    const { data, error } = await supabase.rpc('user_active_pass_scope');
    if (error) throw new Error(error.message);
    return (data || []).map(mapScope);
  },

  async redeemPass(code: string): Promise<RedeemResult> {
    const { data, error } = await supabase.rpc('redeem_school_pass', {
      p_code: code,
    });
    if (error) {
      const token = Object.keys(REDEEM_ERRORS).find((t) => error.message.includes(t));
      throw new Error(token ? REDEEM_ERRORS[token] : error.message);
    }
    return {
      passId: data.pass_id,
      schoolName: data.school_name,
      bundleId: data.bundle_id || null,
      expiresAt: new Date(data.expires_at),
      alreadyMember: !!data.already_member,
    };
  },

  /**
   * A school code the learner arrived with and hasn't redeemed: this browser's
   * copy first, then the one signup stored on the account (which is what
   * survives the activation link opening in another browser). Prefill only;
   * never throws — a failed read just means no prompt.
   */
  async getPendingCode(): Promise<string | null> {
    try {
      const { data } = await supabase.auth.getUser();
      const user = data.user;
      // The local copy only counts for the account it belongs to
      // (readPendingSchoolCode binds it on first signed-in read).
      return (
        readPendingSchoolCode(user?.id ?? null) ??
        normaliseSchoolCode(user?.user_metadata?.[SCHOOL_CODE_METADATA_KEY])
      );
    } catch {
      return null;
    }
  },

  /**
   * Retire a redeemed code from both carriers, but only where it IS that
   * code: a different code still waiting must survive. Best-effort.
   */
  async clearPendingCode(redeemedCode: string): Promise<void> {
    const redeemed = normaliseSchoolCode(redeemedCode);
    if (!redeemed) return;
    clearPendingSchoolCode(redeemed);
    try {
      const { data } = await supabase.auth.getUser();
      if (normaliseSchoolCode(data.user?.user_metadata?.[SCHOOL_CODE_METADATA_KEY]) === redeemed) {
        await supabase.auth.updateUser({ data: { [SCHOOL_CODE_METADATA_KEY]: null } });
      }
    } catch (err) {
      reportError('schoolPassService.clearPendingCode', err);
    }
  },

  // --- Admin (/admin/schools) ---

  async getOverview(): Promise<SchoolPassOverviewRow[]> {
    const { data, error } = await supabase.rpc('get_school_passes_overview');
    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({
      schoolId: r.school_id,
      schoolName: r.school_name,
      contactName: r.contact_name || null,
      contactEmail: r.contact_email || null,
      passId: r.pass_id || null,
      code: r.code || null,
      bundleId: r.bundle_id || null,
      bundleTitle: r.bundle_title || null,
      seatCap: r.seat_cap ?? null,
      seatsUsed: Number(r.seats_used) || 0,
      startsAt: r.starts_at ? new Date(r.starts_at) : null,
      expiresAt: r.expires_at ? new Date(r.expires_at) : null,
      status: r.status || null,
    }));
  },

  async createSchool(input: {
    name: string;
    contactName?: string;
    contactEmail?: string;
    notes?: string;
  }): Promise<{ id: string }> {
    const { data, error } = await supabase.rpc('create_school', {
      p_name: input.name,
      p_contact_name: input.contactName ?? null,
      p_contact_email: input.contactEmail ?? null,
      p_notes: input.notes ?? null,
    });
    if (error) throw new Error(error.message);
    return { id: data.id };
  },

  async createPass(input: {
    schoolId: string;
    expiresAt: Date;
    seatCap?: number | null;
    bundleId?: string | null;
    startsAt?: Date;
  }): Promise<{ id: string; code: string }> {
    const { data, error } = await supabase.rpc('create_school_pass', {
      p_school_id: input.schoolId,
      p_expires_at: input.expiresAt.toISOString(),
      p_seat_cap: input.seatCap ?? null,
      p_bundle_id: input.bundleId ?? null,
      ...(input.startsAt ? { p_starts_at: input.startsAt.toISOString() } : {}),
    });
    if (error) throw new Error(error.message);
    return { id: data.id, code: data.code };
  },

  async revokePass(passId: string): Promise<void> {
    const { error } = await supabase.rpc('revoke_school_pass', { p_pass_id: passId });
    if (error) throw new Error(error.message);
  },

  // --- School reporting (contacts + admins, 20260901000005) ---

  /** Schools the signed-in user is a contact of. Empty = not a contact. */
  async getContactSchools(): Promise<ContactSchool[]> {
    const { data, error } = await supabase.rpc('get_contact_schools');
    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({ schoolId: r.school_id, schoolName: r.school_name }));
  },

  async getSchoolPassSummary(schoolId: string): Promise<SchoolPassSummaryRow[]> {
    const { data, error } = await supabase.rpc('get_school_pass_summary', {
      p_school_id: schoolId,
    });
    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({
      passId: r.pass_id,
      code: r.code,
      bundleId: r.bundle_id || null,
      bundleTitle: r.bundle_title || null,
      seatCap: r.seat_cap ?? null,
      seatsUsed: Number(r.seats_used) || 0,
      startsAt: new Date(r.starts_at),
      expiresAt: new Date(r.expires_at),
      status: r.status,
    }));
  },

  async getSchoolProgress(schoolId: string): Promise<SchoolLearnerRow[]> {
    const { data, error } = await supabase.rpc('get_school_progress', {
      p_school_id: schoolId,
    });
    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({
      userId: r.user_id,
      userName: r.user_name,
      userEmail: r.user_email,
      redeemedAt: new Date(r.redeemed_at),
      coursesStarted: Number(r.courses_started) || 0,
      coursesCompleted: Number(r.courses_completed) || 0,
      avgProgress: Number(r.avg_progress) || 0,
    }));
  },

  async getSchoolCourseProgress(schoolId: string): Promise<SchoolCourseRow[]> {
    const { data, error } = await supabase.rpc('get_school_course_progress', {
      p_school_id: schoolId,
    });
    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({
      courseId: r.course_id,
      courseTitle: r.course_title,
      learners: Number(r.learners) || 0,
      completions: Number(r.completions) || 0,
      avgProgress: Number(r.avg_progress) || 0,
    }));
  },

  // --- Contacts management (admin) ---

  async listSchoolContacts(schoolId: string): Promise<SchoolContact[]> {
    const { data, error } = await supabase
      .from('school_contacts')
      .select('user_id, created_at, users(name, email)')
      .eq('school_id', schoolId);
    if (error) throw new Error(error.message);
    return (data || []).map((r: any) => ({
      userId: r.user_id,
      name: r.users?.name ?? '',
      email: r.users?.email ?? '',
      addedAt: new Date(r.created_at),
    }));
  },

  /** Adds an existing same-tenant account as a school contact by email. */
  async addSchoolContact(schoolId: string, email: string): Promise<SchoolContact> {
    const { data, error } = await supabase.rpc('add_school_contact', {
      p_school_id: schoolId,
      p_email: email,
    });
    if (error) {
      throw new Error(
        error.message.includes('user_not_found')
          ? 'No account with that email exists on this site yet — create it under Students first.'
          : error.message,
      );
    }
    return { userId: data.user_id, name: data.name, email: data.email, addedAt: new Date() };
  },

  async removeSchoolContact(schoolId: string, userId: string): Promise<void> {
    const { error } = await supabase.rpc('remove_school_contact', {
      p_school_id: schoolId,
      p_user_id: userId,
    });
    if (error) throw new Error(error.message);
  },

  /** Records an offline (EFT/invoice) school sale as a completed transaction. */
  async recordPassSale(input: {
    passId: string;
    amount: number;
    reference?: string;
  }): Promise<void> {
    const { error } = await supabase.rpc('record_school_pass_sale', {
      p_pass_id: input.passId,
      p_amount: input.amount,
      p_reference: input.reference ?? null,
    });
    if (error) throw new Error(error.message);
  },
};

export interface ContactSchool {
  schoolId: string;
  schoolName: string;
}

export interface SchoolPassSummaryRow {
  passId: string;
  code: string;
  bundleId: string | null;
  bundleTitle: string | null;
  seatCap: number | null;
  seatsUsed: number;
  startsAt: Date;
  expiresAt: Date;
  status: 'active' | 'revoked';
}

export interface SchoolLearnerRow {
  userId: string;
  userName: string;
  userEmail: string;
  redeemedAt: Date;
  coursesStarted: number;
  coursesCompleted: number;
  avgProgress: number;
}

export interface SchoolCourseRow {
  courseId: string;
  courseTitle: string;
  learners: number;
  completions: number;
  avgProgress: number;
}

export interface SchoolContact {
  userId: string;
  name: string;
  email: string;
  addedAt: Date;
}
